import React, { useEffect, useState } from 'react';

import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import ErrorPanel from '../../shared/components/ErrorPanel';
import Grid from '@mui/material/Unstable_Grid2';
import ProfileFieldInputVm from 'shared/models/ProfileFieldInputVm';
import UserAvatar from '../../membership/components/UserAvatar';
import UserProfileEditor from '../../shared/components/UserProfileEditor';
import { avatarStyles } from '../../shared/hooks/useAvatarStyles';
import { styled } from '@mui/system';
import useUserProfile from '../../shared/hooks/useUserProfile';

interface UserProfileRequiredDialogProps {
  userkey: number;
  open: boolean;
  onClose: () => void;
}

function UserProfileRequiredDialog(props: UserProfileRequiredDialogProps) {
  const [requireProfile, setRequireProfile] = useState<boolean>();
  const {
    handleProfileChange,
    saveFieldValuesAsync,
    loading,
    loadError,
    saving,
    saveError,
    profileFields,
    userProfileFields,
    profileFieldInputs,
    avatarProfileField,
    allRequiredFilledIn
  } = useUserProfile(props.userkey, { onSaveComplete: props.onClose });

  const canSubmit = () => allRequiredFilledIn;

  const handleSubmit = async () => {
    await saveFieldValuesAsync(props.userkey, profileFieldInputs);
  };

  useEffect(() => {
    if (allRequiredFilledIn != null) {
      if (allRequiredFilledIn === true && requireProfile !== true) {
        props.onClose();
      } else {
        if (requireProfile == null) setRequireProfile(true);
      }
    }
  }, [allRequiredFilledIn]);

  const onAvatarChange = (value: string) => {
    const input: ProfileFieldInputVm = {
      ...avatarProfileField,
      value
    };
    handleProfileChange(input);
  };

  const profilekeys = profileFieldInputs
    ?.filter((p) => p.required && !p.value)
    .map((field) => field.profilekey);

  const requiredTitle = profileFields
    ?.filter((p) => profilekeys?.includes(p.profilekey))
    .map((m) => m.title)
    .join(', ');

  const message = requiredTitle
    ? `You’ll need to select the following before you can continue: ${requiredTitle}`
    : 'You can save and continue.';

  return !loading && !!requireProfile ? (
    <Dialog open={props.open} fullWidth maxWidth="sm">
      <DialogTitle>{loading ? 'Required profile data' : message}</DialogTitle>
      <DialogContent>
        <Grid container direction="column" spacing={2}>
          {avatarProfileField?.required && (
            <Grid>
              <UserAvatar
                sx={avatarStyles}
                userkey={props.userkey}
                edit
                required={avatarProfileField.required}
                onAvatarChange={onAvatarChange}
              />
            </Grid>
          )}
          <Grid>
            {requireProfile ? (
              <UserProfileEditor
                userkey={props.userkey}
                hideSubFields
                indicateErrorOnLoad
                loading={loading}
                error={loadError}
                profileFields={profileFields}
                userProfileFields={userProfileFields}
                values={profileFieldInputs}
                onChange={handleProfileChange}
              />
            ) : (
              <p>Checking required profile fields...</p>
            )}
          </Grid>
          {saveError && (
            <Grid>
              <ErrorPanel
                title="Save Profile Information"
                message="There was an error while saving profile information."
              />
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        {!loading && (
          <Button
            variant="outlined"
            color="primary"
            disabled={!canSubmit() || saving}
            onClick={handleSubmit}
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  ) : null;
}

export default UserProfileRequiredDialog;
